import { client } from '@/client';
import { ExportType } from '@/client/reports';
import { DashboardType } from '@/common/constants';
import { useNotifications } from '@/hooks/notifications.hook';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentDashboard } from '@/store/features/dashboard/current-dashboard.slice';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { createContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledContainer = styled(FlexContainer)<{ isAdminView: boolean }>`
  position: fixed;
  bottom: 24px;
  left: ${({ isAdminView }) =>
    isAdminView ? 'max(calc(20% + 24px), 64px)' : '64px'};
  max-width: 292px;
  background-color: var(--white-main);
  border: none;
  border-radius: var(--xsmall-border-radius);
  box-shadow: var(--box-shadow-black);
  z-index: 9999;
  padding: var(--semi-big-padding) var(--default-padding);
`;

const StyledProgressContainer = styled(FlexContainer)`
  justify-content: center;
  align-items: center;
  background-color: var(--beige-main);
  border-radius: var(--xsmall-border-radius);
  padding: var(--small-padding);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 43px;
  max-width: 43px;
  min-height: 43px;
  max-height: 43px;
`;

const Title = styled.h3`
  color: var(--black-main);
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  margin: 0 0 var(--xxxsmall-padding) 0;
  padding: 0;
`;

const Description = styled.p`
  color: var(--black-main);
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  margin: 0;
`;

const StyledDownloadButton = styled(AppButton)`
  background-color: var(--red-main);
  border-color: var(--red-main);
  padding: 0;
  width: 43px;
  height: 43px;
  max-height: 43px;
  align-items: center;
  justify-content: center;
  border-radius: var(--xsmall-border-radius) !important;

  &.p-button {
    padding: 0 !important;
  }
`;

const StyledCloseButton = styled(AppButton)<{ isAdminView: boolean }>`
  padding: 0 !important;
  position: fixed;
  bottom: 101px;
  left: ${({ isAdminView }) =>
    isAdminView
      ? 'max(calc(20% + 24px + 256px), calc(64px + 256px))'
      : 'calc(64px + 256px)'};
  width: var(--medium-button-height) !important;
  height: var(--medium-button-height) !important;

  &.p-button.p-button-text {
    background: transparent !important;
    color: var(--gray-darker) !important;
  }
`;

const StyledProgressSpinner = styled(ProgressSpinner)`
  width: 23px;
  height: 23px;

  .p-progress-spinner-circle {
    stroke: var(--red-main) !important;
  }
`;

const DownloadIcon = styled.i`
  font-size: var(--medium-font-size);
`;

type ReportExportState = {
  setType: React.Dispatch<React.SetStateAction<ExportType | undefined>>;
  setExportKey: React.Dispatch<React.SetStateAction<string | undefined>>;
  setIsDownloading: React.Dispatch<React.SetStateAction<boolean>>;
  setNotificationParam: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  isConnected: React.MutableRefObject<boolean>;
  isDownloading: boolean;
};

export const ExportContext = createContext<ReportExportState>({
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setType: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setExportKey: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setIsDownloading: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setNotificationParam: () => {},
  isConnected: { current: false },
  isDownloading: false,
});

export const ReportMessage: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const selectedDashboard = useAppSelector(selectCurrentDashboard);
  const isAdminView = selectedDashboard === DashboardType.ADMIN;

  const [type, setType] = useState<ExportType | undefined>(undefined);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [downloadUrl, setDownloadUrl] = useState<string | undefined>();
  const [progress, setProgress] = useState<number>(0);
  const [exportKey, setExportKey] = useState<string>();

  const notifyUrl =
    type === ExportType.SUMMARY ||
    type === ExportType.AWARENESS ||
    type === ExportType.EXECUTIVE_SUMMARY
      ? client.reports.reportsNotifyUrl
      : type === ExportType.PHISHING
      ? client.campaigns.campaignsNotifyUrl
      : type === ExportType.ACCOUNT_USERS || type === ExportType.ACCOUNTS
      ? client.accounts.accountsNotifyUrl
      : type === ExportType.BRANCH_USERS
      ? client.branches.branchesNotifyUrl
      : client.groups.groupsNotifyUrl;

  const { lastMessage, setNotificationParam, notificationParam, isConnected } =
    useNotifications(notifyUrl);

  useEffect(() => {
    if (!notificationParam) return;
    setDownloadUrl(undefined);
    handleSyncNotification(lastMessage);
  }, [JSON.stringify(lastMessage)]);

  const handleSyncNotification = async (messages: { data: any } | null) => {
    if (
      !messages?.data?.event ||
      ![
        'awareness-report.export.finished',
        'awareness-report.export.progress',
        'summary-report.export.finished',
        'summary-report.export.progress',
        'executive-summary-report.export.finished',
        'executive-summary-report.export.failed',
        'campaign-results.export.finished',
        'campaign-results.export.progress',
        'users.export.finished',
        'users.export.progress',
        'users.export.failed',
        'accounts.export.finished',
        'accounts.export.progress',
        'accounts.export.failed',
      ].includes(messages?.data?.event) ||
      (messages.data?.exportKey !== exportKey &&
        messages.data.payload?.exportKey !== exportKey)
    )
      return;

    if (
      [
        'awareness-report.export.progress',
        'summary-report.export.progress',
        'campaign-results.export.progress',
        'users.export.progress',
        'accounts.export.progress',
      ].includes(messages.data.event) &&
      (messages.data?.exportKey === exportKey ||
        messages.data.payload?.exportKey === exportKey)
    ) {
      const progress =
        type === ExportType.AWARENESS || type === ExportType.SUMMARY
          ? messages.data.progress
          : messages.data.payload.progress;

      setProgress(progress);
      return;
    }

    if (
      [
        'awareness-report.export.finished',
        'summary-report.export.finished',
        'executive-summary-report.export.finished',
        'campaign-results.export.finished',
        'users.export.finished',
        'accounts.export.finished',
      ].includes(messages.data.event) &&
      (messages.data?.exportKey === exportKey ||
        messages.data.payload?.exportKey === exportKey)
    ) {
      const downloadUrl =
        type === ExportType.AWARENESS ||
        type === ExportType.SUMMARY ||
        type === ExportType.EXECUTIVE_SUMMARY
          ? messages.data.downloadUrl
          : messages.data.payload.downloadUrl;

      setDownloadUrl(downloadUrl);
      setIsDownloading(false);
      setProgress(0);
    }

    if (messages.data.event === 'users.export.failed') {
      toast?.error(t('toast.error'), messages.data.payload.error);
    }

    if (messages.data.event === 'accounts.export.failed') {
      toast?.error(t('toast.error'), messages.data.payload.error);
    }

    if (messages.data.event === 'executive-summary-report.export.failed') {
      toast?.error(
        t('toast.error'),
        messages.data?.error ?? messages.data.payload?.error,
      );
    }

    setNotificationParam(undefined);
    setExportKey(undefined);
    setIsDownloading(false);
    setProgress(0);
  };

  const onDownload = () => {
    if (!downloadUrl) return;
    window.open(downloadUrl, '_self');
    setDownloadUrl(undefined);
    setIsDownloading(false);
    setProgress(0);
  };

  return (
    <ExportContext.Provider
      value={{
        setType,
        setExportKey,
        setIsDownloading,
        setNotificationParam,
        isConnected,
        isDownloading,
      }}
    >
      {(downloadUrl || isDownloading) && (
        <StyledContainer
          justify="space-between"
          align="center"
          isAdminView={isAdminView}
        >
          <StyledCloseButton
            isAdminView={isAdminView}
            icon="pi pi-times"
            severity="success"
            type="text"
            rounded
            onClick={() => {
              setDownloadUrl(undefined);
              setIsDownloading(false);
              setProgress(0);
            }}
          />
          <FlexContainer direction="column" align="flex-start" className="pr-2">
            <Title>
              {!downloadUrl && isDownloading
                ? t('reports.message.generatingReport')
                : t('reports.message.readyForDownload')}
            </Title>
            <Description>
              {!downloadUrl && isDownloading
                ? progress > 0
                  ? t('reports.message.generatingReport.progress', {
                      progress,
                    })
                  : t('reports.message.generatingReportDescription')
                : t('reports.message.linkExpiresIn')}
            </Description>
          </FlexContainer>
          {!downloadUrl && isDownloading ? (
            <StyledProgressContainer>
              <StyledProgressSpinner strokeWidth="8" animationDuration="1.5s" />
            </StyledProgressContainer>
          ) : (
            <StyledDownloadButton
              aria-label={t('reports.message.downloadReport')}
              onClick={onDownload}
              severity="success"
              className=".p-button-icon-only"
            >
              <DownloadIcon className="pi pi-download" />
            </StyledDownloadButton>
          )}
        </StyledContainer>
      )}
      {children}
    </ExportContext.Provider>
  );
};
